import { Card, CardContent } from "@/components/ui/card";
import { Typography, Button } from "@castlery/fortress";
import { Star, StarFilled } from "@/components/icons";
import { PreviewImage } from "@/components/ui/image";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/utils";
import { UtmUrlHelper } from "@/utils/utm";
import { useChatStore } from "@/stores/chatStore";
import { generateArray } from "@/utils/array";
import {
  CLICK_ACTION,
  TRACK_EVENT,
  LINK_TAG,
  trackEvent,
} from "@/utils/analytics";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";
import { useMessageContext } from "./MessageContext";

const ProductReviewWithoutErrorBoundary = ({ data }: { data: any }) => {
  const { handleProductClick } = useMessageContext();

  const utmLink = UtmUrlHelper.generateUtmUrl(
    data.product_link,
    data.product_name,
  );

  const handleProductNameClick = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "product_review",
      action: CLICK_ACTION,
      tag: LINK_TAG,
      tag_value: "product_review_link",
    });
    handleProductClick(data.product_sku_code);
  };

  return (
    <Card
      className={cn("p-4 w-full rounded-md", {
        "rounded-tl-none": true,
        "border border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E]": true,
      })}
    >
      <CardContent className="p-0 space-y-3">
        <div className="flex items-center gap-4">
          {/* 商品图片 */}
          <div className="flex-shrink-0 w-[100px] h-[100px]">
            <PreviewImage src={data.product_pic} alt={data.product_name} />
          </div>

          {/* 商品信息 */}
          <div className="space-y-3">
            {/* 商品名称 */}
            <a
              className="line-clamp-2 cursor-pointer"
              href={utmLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleProductNameClick}
            >
              <Typography level="body2">{data.product_name}</Typography>
            </a>

            {/* 商品价格 */}
            <div>
              <div className="flex items-center gap-2">
                <Typography level="body2">
                  {formatCurrency(data.product_price, data.currency)}
                </Typography>
                {data.product_original_price !== data.product_price && (
                  <Typography
                    level="body2"
                    className="text-[#9E9E9E] line-through"
                  >
                    {formatCurrency(data.product_original_price, data.currency)}
                  </Typography>
                )}
              </div>
              {data.sale_quantity_min > 1 && (
                <Typography level="caption1" className="text-[#9E9E9E]">
                  (Set of {data.sale_quantity_min})
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* 商品评分 */}
        <div className="flex items-center justify-center gap-3 p-3 bg-[#F6F3E7]">
          <div className="text-[28px] leading-[1.2]">
            {data.product_rating || 5}
          </div>
          <div>
            <div className="flex items-center">
              {generateArray({
                length: 5,
                start: 1,
              }).map((star) => (
                <div key={star} className="relative text-primary">
                  {/* 背景星星（灰色） */}
                  <Star className="!w-5 !h-5" />
                  {/* 前景星星（根据评分显示） */}
                  <div
                    className="absolute top-0 left-0 overflow-hidden"
                    style={{
                      width: `${
                        Math.min(
                          Math.max(data.product_rating - (star - 1), 0),
                          1,
                        ) * 100
                      }%`,
                    }}
                  >
                    <StarFilled className="!w-5 !h-5" />
                  </div>
                </div>
              ))}
            </div>
            <Typography level="caption1" className="text-[#7c5f68]">
              Based on {data.product_rating_quantity || 0} ratings
            </Typography>
          </div>
        </div>

        {/* 查看所有评价 */}
        <Button
          className="w-full"
          onClick={() => {
            trackEvent({
              event: TRACK_EVENT,
              category: "product_review",
              action: CLICK_ACTION,
              tag: LINK_TAG,
              tag_value: "product_all_reviews_link",
            });
            window.open(utmLink, "_blank");
          }}
        >
          SEE ALL REVIEWS
        </Button>
      </CardContent>
    </Card>
  );
};

export const ProductReview = ({ data }: { data: any }) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <ProductReviewWithoutErrorBoundary data={data} />
    </ErrorBoundary>
  );
};
